import React, { useEffect, useState } from "react";
import {
  X,
  Clock,
  User,
  CheckCircle2,
  AlertCircle,
  FileText,
  StickyNote,
} from "lucide-react";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../config/firebase";
import { APP_ID } from "../config/constants";

interface Log {
  id: string;
  text: string;
  user?: string;
  timestamp: any;
  type?: "checklist" | "status" | "handover" | "blocked" | "info" | "note";
}

interface HistoryPanelProps {
  caseId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function HistoryPanel({
  caseId,
  isOpen,
  onClose,
}: HistoryPanelProps) {
  const [logs, setLogs] = useState<Log[]>([]);

  useEffect(() => {
    if (!caseId || !isOpen) return;

    const logsRef = collection(
      db,
      "artifacts",
      APP_ID,
      "public",
      "data",
      "onboarding_cases",
      caseId,
      "history",
    );
    const q = query(logsRef, orderBy("timestamp", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedLogs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Log[];
      setLogs(fetchedLogs);
    });

    return () => unsubscribe();
  }, [caseId, isOpen]);

  const formatTime = (timestamp: any) => {
    if (!timestamp) return "";
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleString();
  };

  const getIcon = (type: string | undefined) => {
    switch (type) {
      case "checklist":
        return <CheckCircle2 size={14} className="text-blue-500" />;
      case "blocked":
        return <AlertCircle size={14} className="text-red-500" />;
      case "handover":
        return <FileText size={14} className="text-purple-500" />;
      case "note":
        return <StickyNote size={14} className="text-amber-500" />;
      default:
        return <Clock size={14} className="text-gray-400" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-96 bg-white dark:bg-slate-900 shadow-2xl z-[60] border-l border-gray-200 dark:border-slate-700 flex flex-col transform transition-transform duration-300 ease-in-out">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50 dark:bg-slate-900">
        <h2 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <Clock size={18} /> Activity History
        </h2>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-200 dark:hover:bg-slate-800 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {logs.length === 0 && (
          <div className="text-center text-gray-400 py-8 text-sm">
            No activity recorded yet.
          </div>
        )}

        {logs.map((log) => (
          <div key={log.id} className="relative pl-6 pb-2 last:pb-0">
            {/* Timeline Line */}
            <div className="absolute left-2 top-2 bottom-0 w-px bg-gray-100 dark:bg-slate-800 last:hidden"></div>

            {/* Timeline Dot */}
            <div className="absolute left-0 top-1.5 w-4 h-4 bg-white dark:bg-slate-900 border-2 border-gray-100 dark:border-slate-700 rounded-full flex items-center justify-center z-10">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-slate-600"></div>
            </div>

            <div className="bg-gray-50 dark:bg-slate-800/50 p-3 rounded-lg border border-gray-100 dark:border-slate-700/50">
              <div className="flex items-start gap-2 mb-1">
                <div className="mt-0.5">{getIcon(log.type)}</div>
                <div className="text-sm text-slate-700 dark:text-slate-200 font-medium leading-snug w-full">
                  {log.type === "note" ? (
                    <details className="group">
                      <summary className="cursor-pointer list-none flex items-center gap-1 font-bold text-amber-600 dark:text-amber-500 text-xs uppercase tracking-wide">
                        Note Updated{" "}
                        <span className="text-[10px] opacity-50 group-open:rotate-90 transition-transform">
                          ▶
                        </span>
                      </summary>
                      <div
                        className="mt-2 p-2 bg-white dark:bg-slate-900 rounded border border-gray-100 dark:border-slate-700 text-xs font-mono max-h-40 overflow-y-auto"
                        dangerouslySetInnerHTML={{ __html: log.text }}
                      />
                    </details>
                  ) : (
                    log.text
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 text-[10px] text-gray-400 pl-6">
                {log.user && (
                  <span className="flex items-center gap-1">
                    <User size={10} /> {log.user.split("@")[0]}
                  </span>
                )}
                <span>{formatTime(log.timestamp)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
